import React, { useState } from 'react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';
import { API_CONFIG } from '../../config/api';
import { getHeaders } from '../../utils/api';
import './FcmManagement.css';

const DirectMessage = () => {
  const [englishTitle, setEnglishTitle] = useState('');
  const [englishDescription, setEnglishDescription] = useState('');
  const [germanTitle, setGermanTitle] = useState('');
  const [germanDescription, setGermanDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [translating, setTranslating] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const translateNotification = async () => {
    if (!englishTitle.trim() || !englishDescription.trim()) return;
    try {
      setTranslating(true);
      setError(null);
      setSuccess(null);
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FCM.TRANSLATE_NOTIFICATION}`,
        { title: englishTitle, description: englishDescription },
        { headers: getHeaders() }
      );
      setGermanTitle(response.data.title);
      setGermanDescription(response.data.description);
      setSuccess('German translation is ready. You can edit it before sending.');
    } catch (err) {
      setError('Failed to translate the message.');
    } finally {
      setTranslating(false);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await axios.post(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FCM.UPLOAD_IMAGE}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.url;
  };

  const sendMessage = async () => {
    try {
      setSending(true);
      setError(null);
      setSuccess(null);
      const finalImageUrl = selectedFile ? await uploadImage(selectedFile) : imageUrl;
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FCM.SEND_LOCALIZED_NOTIFICATION}`,
        {
          englishTitle,
          englishDescription,
          germanTitle,
          germanDescription,
          imageUrl: finalImageUrl,
        },
        { headers: getHeaders() }
      );
      setSuccess(
        `Direct message accepted by ${response.data.successfulTopics} of ${response.data.targetedTopics} topics ` +
        `(${response.data.englishTopics} English, ${response.data.germanTopics} German).`
      );
      setEnglishTitle('');
      setEnglishDescription('');
      setGermanTitle('');
      setGermanDescription('');
      setImageUrl('');
      setSelectedFile(null);
    } catch (err) {
      const serverMessage = axios.isAxiosError(err) && typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : null;
      setError(serverMessage || 'Failed to send the direct message.');
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  const readyToSend = Boolean(
    englishTitle.trim() && englishDescription.trim() && germanTitle.trim() && germanDescription.trim()
  );

  return (
    <div className="fcm-management direct-message-management">
      <h2>Direct Message</h2>
      <p className="broadcast-location-note">
        Send one localized message to all users through their language-specific FCM topics.
      </p>

      {error && <div className="error-message" role="alert">{error}</div>}
      {success && <div className="success-message" role="status">{success}</div>}

      <div className="localized-notification-editor">
        <section className="language-editor" aria-labelledby="direct-message-english-heading">
          <h3 id="direct-message-english-heading">English</h3>
          <div className="form-group">
            <label htmlFor="direct-message-english-title">Title:</label>
            <input id="direct-message-english-title" type="text" value={englishTitle}
              onChange={(event) => setEnglishTitle(event.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="direct-message-english-description">Description:</label>
            <textarea id="direct-message-english-description" value={englishDescription}
              onChange={(event) => setEnglishDescription(event.target.value)} />
          </div>
        </section>

        <div className="translation-action">
          <button type="button" onClick={translateNotification}
            disabled={translating || !englishTitle.trim() || !englishDescription.trim()}>
            {translating ? 'Translating…' : 'Translate →'}
          </button>
        </div>

        <section className="language-editor" aria-labelledby="direct-message-german-heading">
          <h3 id="direct-message-german-heading">Deutsch</h3>
          <div className="form-group">
            <label htmlFor="direct-message-german-title">Titel:</label>
            <input id="direct-message-german-title" type="text" value={germanTitle}
              onChange={(event) => setGermanTitle(event.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="direct-message-german-description">Beschreibung:</label>
            <textarea id="direct-message-german-description" value={germanDescription}
              onChange={(event) => setGermanDescription(event.target.value)} />
          </div>
        </section>
      </div>

      <div className="form-group">
        <label>Image:</label>
        <div className="image-options-column">
          <div className="image-option">
            <input type="text" placeholder="Image URL" value={imageUrl}
              onChange={(event) => setImageUrl(event.target.value)} disabled={Boolean(selectedFile)} />
          </div>
          <div className="image-option">
            <span>OR</span>
            <input type="file" accept="image/*" disabled={Boolean(imageUrl)}
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setSelectedFile(file);
                if (file) setImageUrl('');
              }} />
          </div>
        </div>
      </div>

      <button type="button" className="send-button" onClick={() => setConfirmOpen(true)}
        disabled={sending || translating || !readyToSend}>
        {sending ? 'Sending…' : 'Send Direct Message'}
      </button>

      <ConfirmDialog
        isOpen={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={sendMessage}
        title="Confirm Direct Message"
        message={`Send "${englishTitle}" to every national notification topic? Users receive the localized copy for their subscribed language.`}
        confirmText="Send"
        cancelText="Cancel"
        type="approve"
      />
    </div>
  );
};

export default DirectMessage;
